import { Link } from 'react-router';
import { LIBELLE_STATUT_CALCUL, LIBELLE_STATUT_VARIABLE, type BlocCalculPaie, type StatutCalculPaie, type StatutVariablePaie } from '@/api/types';
import { Kpi } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, dateFr, montant } from '@/lib/utils';

export const StatutCalculBadge = ({ statut, simulation }: { statut: StatutCalculPaie; simulation: boolean }) => (
  <span className="inline-flex flex-wrap gap-1">
    <Badge variant="outline" className={cn(simulation ? 'border-sky-200 bg-sky-50 text-sky-800' : 'border-indigo-200 bg-indigo-50 text-indigo-800')}>{simulation ? 'Simulation' : 'Calcul réel'}</Badge>
    <Badge variant={statut === 'CLOTUREE' || statut === 'PAYEE' ? 'default' : statut === 'ANNULE' ? 'destructive' : statut === 'BROUILLON' ? 'secondary' : 'outline'} className={cn((statut === 'FIGE' || statut === 'SOUMISE' || statut === 'CONTROLEE_RH' || statut === 'VALIDEE_DRH' || statut === 'VISEE_DGA_CG') && 'border-amber-200 bg-amber-50 text-amber-800', (statut === 'APPROUVEE_DF' || statut === 'EN_PAIEMENT') && 'border-emerald-200 bg-emerald-50 text-emerald-800')}>{LIBELLE_STATUT_CALCUL[statut]}</Badge>
  </span>
);

export const StatutVariableBadge = ({ statut }: { statut: StatutVariablePaie }) => (
  <Badge variant="outline" className={cn('whitespace-nowrap', statut === 'VALIDEE' && 'border-emerald-200 bg-emerald-50 text-emerald-800', statut === 'SAISIE' && 'border-amber-200 bg-amber-50 text-amber-800', (statut === 'REFUSEE' || statut === 'ANNULEE') && 'border-slate-200 bg-slate-100 text-slate-600')}>
    {LIBELLE_STATUT_VARIABLE[statut]}
  </Badge>
);

/** Bloc du tableau de bord (lot HP-4) : dernier calcul, variables à valider, accès au calcul de paie ; profils de la paie, DF, DG, contrôle de gestion et chef comptable. */
export function BlocCalculPaieTableau({ bloc }: { bloc: BlocCalculPaie }) {
  const d = bloc.dernier;
  if (!d && !bloc.gerer && !bloc.voirBulletins) {
    return (
      <Card data-testid="bloc-mon-bulletin">
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center justify-between gap-2">
            Mes bulletins de paie
            <Button asChild size="sm" variant="outline">
              <Link to="/rh/mes-bulletins">Mes bulletins</Link>
            </Button>
          </CardTitle>
          <CardDescription>Bulletins publiés à la clôture de chaque paie, consultables et téléchargeables en PDF.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Link to="/rh/mes-bulletins">
            <Kpi label="Dernier bulletin" valeur={bloc.mien ? bloc.mien.mois : 'aucun'} detail={bloc.mien ? `publié le ${dateFr(bloc.mien.clotureLe)}` : 'après la première paie clôturée'} accent={bloc.mien ? 'ok' : undefined} />
          </Link>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card data-testid="bloc-calcul-paie">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          Calcul de paie
          <span className="flex flex-wrap gap-2">
            <Button asChild size="sm" variant="outline">
              <Link to="/paie/calculs">Calculs et bulletins</Link>
            </Button>
            {bloc.voirBulletins && (
              <Button asChild size="sm" variant="outline">
                <Link to="/paie/calculs?onglet=variables">Variables</Link>
              </Button>
            )}
          </span>
        </CardTitle>
        <CardDescription>Simulations libres, calcul réel sur la période de présence clôturée, bulletins par agent, livre de paie ; figé par la paie avec son taux de change avant le circuit de validation.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Link to={d ? `/paie/calculs?calcul=${d.id}` : '/paie/calculs'}>
          <Kpi label={d?.aDecider ? 'Paie à décider' : 'Dernier calcul'} valeur={d ? `${d.simulation ? 'Simulation' : 'Paie'} ${d.mois}` : 'aucun'} detail={d ? `n° ${d.numero} · ${LIBELLE_STATUT_CALCUL[d.statut]} · ${d.bulletins} bulletin(s) · calculé le ${dateFr(d.calculeLe)}${d.aDecider ? ' · votre décision est attendue' : ''}` : bloc.gerer ? 'lancez une simulation sur la période en cours' : 'en attente de la paie'} accent={d?.aDecider ? 'alerte' : d?.statut === 'CLOTUREE' || d?.statut === 'FIGE' ? 'ok' : undefined} />
        </Link>
        <Link to={d ? `/paie/calculs?calcul=${d.id}` : '/paie/calculs'}>
          <Kpi label="Net à payer" valeur={d ? montant(d.netAPayer, true) : '—'} detail={d ? `coût employeur ${montant(d.coutEmployeur, true)}` : 'après calcul'} />
        </Link>
        <Link to={d ? `/paie/calculs?calcul=${d.id}&anomalies=bloques` : '/paie/calculs'}>
          <Kpi label="Bulletins bloqués" valeur={d ? d.bloques : '—'} detail="fiche, barème, impôt ou taux à régulariser avant de figer" accent={d && d.bloques > 0 ? 'alerte' : d ? 'ok' : undefined} />
        </Link>
        {bloc.voirBulletins && (
          <Link to={`/paie/calculs?onglet=variables${bloc.periode ? `&mois=${bloc.periode.mois}` : ''}`}>
            <Kpi label={bloc.variablesAValider !== null ? 'Variables à valider' : 'Variables de la période'} valeur={bloc.variablesAValider ?? bloc.variablesSaisies} detail={bloc.periode ? `période ${bloc.periode.mois} · ${bloc.variablesSaisies} saisie(s) ou validée(s)` : 'aucune période ouverte'} accent={bloc.variablesAValider ? 'alerte' : undefined} />
          </Link>
        )}
      </CardContent>
    </Card>
  );
}
